import React, { useCallback, useEffect, useMemo } from "react";
import { AHSeparator, AHText, Box, useLayoutContext } from "arkh";
import { FlatList, TextInputProps } from "react-native";
import {
  PreloadedQuery,
  fetchQuery,
  graphql,
  usePreloadedQuery,
  useQueryLoader,
  useRelayEnvironment,
} from "react-relay";
import { ProductSearchQuery } from "./__generated__/ProductSearchQuery.graphql";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useKeyboardInModal } from "../Files/useKeyboardInModal";
import { ProductSearchListItem } from "./ProductSearchListItem";
import { FormattedMessage } from "react-intl";
import { SearchInput } from "./SearchInput";
import { BottomSheetFlatList } from "@gorhom/bottom-sheet";
import { ProductListItemProduct } from "./ProductListItem";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";

export function ProductSearchNoResults() {
  return (
    <Box
      cornerRadiusBottomRight="large"
      cornerRadiusBottomLeft="large"
      color="surface-primary"
      style={{
        height: 80,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <AHText type="subtitle" color="text-secondary">
        <FormattedMessage
          defaultMessage="No products found in search."
          description="ProductSearch no products found"
        />
      </AHText>
    </Box>
  );
}

export function ProductSearchInitialEmptyState() {
  return (
    <Box
      cornerRadiusBottomRight="large"
      cornerRadiusBottomLeft="large"
      style={{
        height: 80,
        justifyContent: "center",
        alignItems: "center",
      }}
      color="surface-primary"
    >
      <AHText type="subtitle" color="text-secondary">
        <FormattedMessage
          defaultMessage="Enter a product name to begin your search."
          description="ProductSearch begin search"
        />
      </AHText>
    </Box>
  );
}

const PRODUCT_SEARCH_QUERY = graphql`
  query ProductSearchQuery($query: String!) {
    currentStore @required(action: THROW) {
      productSearch(query: $query) {
        hits {
          id
          product {
            id
          }
          ...ProductSearchListItem_storeProductSearchHit
        }
      }
    }
  }
`;

function ProductSearchList(props: {
  queryRef: PreloadedQuery<ProductSearchQuery>;
  onPressProduct: (product: ProductListItemProduct) => void;
  productIsDisabled?: (id: string) => string | undefined;
  inBottomSheet?: boolean;
}) {
  const data = usePreloadedQuery(PRODUCT_SEARCH_QUERY, props.queryRef);
  const insets = useSafeAreaInsets();
  const layout = useLayoutContext();
  const [keyboardBottomInset, { onLayout }] = useKeyboardInModal();
  const searchResult = useMemo(() => {
    return data?.currentStore.productSearch.hits ?? [];
  }, [data?.currentStore.productSearch.hits]);

  const paddingBottom = useMemo(
    () => Math.max(keyboardBottomInset, insets.bottom),
    [insets.bottom, keyboardBottomInset]
  );

  type Item = (typeof searchResult)[number];
  const renderItem = useCallback(
    ({ item, index }: { item: Item; index: number }) => {
      const disabledReason = props.productIsDisabled?.(item.product.id);
      return (
        <ProductSearchListItem
          disabled={disabledReason !== undefined}
          disabledReason={disabledReason}
          isLast={searchResult.length - 1 === index}
          isFirst={false}
          onPressProduct={props.onPressProduct}
          product={item}
        />
      );
    },
    [props, searchResult.length]
  );

  const commonProps = {
    ListEmptyComponent: <ProductSearchNoResults />,
    ItemSeparatorComponent: AHSeparator,
    onLayout,
    keyboardShouldPersistTaps: "handled" as const,
    keyboardDismissMode: "none" as const,
    style: {
      flex: 1,
    },
    contentContainerStyle: {
      paddingBottom,
      paddingHorizontal: props.inBottomSheet ? 0 : layout.space.medium,
    },
    scrollIndicatorInsets: {
      bottom: paddingBottom,
    },
    data: searchResult,
    renderItem: renderItem,
  };

  if (props.inBottomSheet) {
    return (
      <BottomSheetFlatList
        {...commonProps}
        keyExtractor={(item: Item, index: number) => item.id ?? "" + index}
      />
    );
  }

  return (
    <FlatList
      {...commonProps}
      keyExtractor={(item, index) => item.id ?? "" + index}
      renderScrollComponent={(props) => <KeyboardAwareScrollView {...props} />}
    />
  );
}

export function ProductSearch({
  query,
  ...props
}: {
  query: string;
  onPressProduct: (product: ProductListItemProduct) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onChange?: TextInputProps["onChange"];
  productIsDisabled?: (id: string) => string | undefined;
  showCancel?: boolean;
  isSearching: boolean;
  autoFocus?: boolean;
  inBottomSheet?: boolean;
}) {
  const environment = useRelayEnvironment();
  const [queryRef, loadQuery] =
    useQueryLoader<ProductSearchQuery>(PRODUCT_SEARCH_QUERY);

  const search = useCallback(
    (query: string) => {
      fetchQuery(environment, PRODUCT_SEARCH_QUERY, { query }).subscribe({
        complete: () => {
          loadQuery({ query }, { fetchPolicy: "store-only" });
        },
      });
    },
    [environment, loadQuery]
  );

  useEffect(() => {
    if (query !== "") {
      search(query);
    }
  }, [query, search]);

  const showInitialEmptyState = useMemo(() => {
    return query === "" || queryRef === null || queryRef === undefined;
  }, [query, queryRef]);

  return (
    <>
      <Box
        spaceHorizontal={props.inBottomSheet ? "none" : "medium"}
        spaceTop={props.inBottomSheet ? "none" : "medium"}
      >
        <SearchInput
          query={query}
          onChange={props.onChange}
          onFocus={props.onFocus}
          onBlur={props.onBlur}
          isSearching={props.isSearching}
          autoFocus={props.autoFocus}
          showCancel={props.showCancel}
        />
      </Box>
      {props.isSearching ? (
        showInitialEmptyState ? (
          <Box spaceHorizontal={props.inBottomSheet ? "none" : "medium"}>
            <ProductSearchInitialEmptyState />
          </Box>
        ) : (
          <ProductSearchList
            queryRef={queryRef!}
            onPressProduct={props.onPressProduct}
            productIsDisabled={props.productIsDisabled}
            inBottomSheet={props.inBottomSheet}
          />
        )
      ) : null}
    </>
  );
}
